import React from 'react'

import DOMPurify from 'dompurify'

import {
  AlignItems,
  Box,
  Color,
  Display,
  Divider,
  FlexDirection,
  FontFamily,
  FontWeight,
  GridDisplay,
  Icon,
  IconVariant,
  JustifyContent,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { BasketSummaryContentBlock, BasketSummaryRow } from '@library/api-menu-experience'

const ValueBannerMessage = withCitrusTheme(Text, (theme) => ({
  '& span': {
    color: theme.color(Color.Success_900),
    fontWeight: FontWeight.Bold,
  },
}))

const SummaryRow = ({ row }: { row: BasketSummaryRow }) => (
  <Box display={Display.Flex} justifyContent={JustifyContent.SpaceBetween} gap={4}>
    <Text>{row.label}</Text>
    <Text>{row.price}</Text>
  </Box>
)

type BasketSummaryProps = {
  contentBlock: BasketSummaryContentBlock
}

export const BasketSummary = ({ contentBlock }: BasketSummaryProps) => (
  <Box
    bg={Color.White}
    borderRadius={1.5}
    boxShadow="0 1px 6px rgba(0, 0, 0, 0.12)"
    color={Color.ColdGrey_900}
    data-testid="basket-summary"
    paddingH={4}
    paddingV={4}
  >
    <Box display={Display.Flex} alignItems={AlignItems.Center} gap={2}>
      <Icon name="payment" variant={IconVariant.Default} size={5} />
      <Text fontWeight={FontWeight.Bold}>{contentBlock.title}</Text>
    </Box>

    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={3}
      style={{ paddingTop: '0.5rem' }}
    >
      <Text size={2} color={Color.ColdGrey_600}>
        {contentBlock.description}
      </Text>

      {contentBlock.recipeItems.map((recipe, index) => (
        <Box
          key={`${recipe.id}-${index}`}
          display={GridDisplay.Grid}
          gridTemplateColumns="minmax(0, 68%) minmax(0, 1fr)"
          gap={4}
          style={{ alignItems: 'start' }}
        >
          <Box minWidth="0">
            <Text
              data-testid={`basket-summary-recipe-title-${index}`}
              fontWeight={FontWeight.Bold}
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {recipe.title}
            </Text>
            {recipe.description && (
              <Text color={Color.ColdGrey_600} size={2}>
                {recipe.description}
              </Text>
            )}
          </Box>
          <Text
            data-testid={`basket-summary-recipe-price-${index}`}
            fontWeight={FontWeight.Bold}
            style={{ justifySelf: 'end' }}
          >
            {recipe.price}
          </Text>
        </Box>
      ))}

      {contentBlock.discount && (
        <Box
          bg={Color.Success_50}
          borderRadius={1.5}
          data-testid="basket-summary-discount"
          display={Display.Flex}
          justifyContent={JustifyContent.SpaceBetween}
          paddingH={3}
          paddingV={2}
        >
          <Text color={Color.Success_800} fontWeight={FontWeight.Bold}>
            {contentBlock.discount.label}
          </Text>
          <Text color={Color.Success_800} fontWeight={FontWeight.Bold}>
            {contentBlock.discount.price}
          </Text>
        </Box>
      )}

      <Divider />
      {contentBlock.surcharge && <SummaryRow row={contentBlock.surcharge} />}
      {contentBlock.products && <SummaryRow row={contentBlock.products} />}
      <SummaryRow row={contentBlock.delivery} />
    </Box>

    <Box paddingBottom={0} paddingV={2}>
      <Divider />
      <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={3}>
        <Box
          display={Display.Flex}
          justifyContent={JustifyContent.SpaceBetween}
          paddingV={2}
          paddingBottom={0}
        >
          <Text fontWeight={FontWeight.Bold}>{contentBlock.total.label}</Text>
          <Box display={Display.Flex} gap={2}>
            {contentBlock.total.originalPrice && (
              <Text style={{ textDecoration: 'line-through' }}>
                {contentBlock.total.originalPrice}
              </Text>
            )}
            <Text
              color={contentBlock.discount ? Color.Success_800 : undefined}
              data-testid="basket-summary-final-total"
              fontWeight={FontWeight.Bold}
            >
              {contentBlock.total.price}
            </Text>
          </Box>
        </Box>

        {contentBlock.valueBanner && (
          <Box
            alignItems={AlignItems.Center}
            bg={Color.Success_50}
            color={Color.Success_900}
            data-testid="basket-summary-value-banner"
            display={Display.Flex}
            gap={2}
            paddingH={4}
            paddingV={3}
            style={{ borderRadius: '8px' }}
          >
            <Icon name="offer_pound" size={8} variant={IconVariant.Inherit} />
            <ValueBannerMessage
              color={Color.ColdGrey_800}
              fontFamily={FontFamily.Bold}
              fontWeight={FontWeight.Bold}
              size={4}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(contentBlock.valueBanner.descriptionHTML),
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  </Box>
)
